/**
 * **模板文件 ⇄ 画布** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * ```text
 *   存下来   `toDocument(当前节点/边)` + 调参面板设置 → 一份 JSON（下载 / 进库）
 *   装回去   `fromDocument` → 重建节点/边 + 套用设置
 * ```
 *
 * 文件格式的**判断逻辑全在 `graph/templateFile.ts`**（纯函数、有单测），
 * 这里只做浏览器那一侧：下载、序列化、应用。
 *
 * ## ⚠️ 2027：**"打开文件"那条路搬走了**（去 `Playground.openDocumentFile`）
 *
 * 原因是文档库：打开一份文件不再只是"把图装进来"，而是
 * 「**进库 + 变成当前文档 + 撞 UUID 时问一句**」—— 那需要库的状态，
 * 而这个 composable 不认识库（它只有画布）。
 *
 * 所以这里只留两件纯粹的、和"库"无关的事：
 *   · `currentFile()` / `saveTemplateFile()` —— 画布 → 文件
 *   · `applyFile()` —— 文件 → 画布（**不弹确认**：该不该问是调用方的事）
 *
 * ## 两条纪律（原来的注释，仍然有效）
 *
 * 1. **装之前先整体校验**（那一步在上游：`parseTemplateFile` 过了才调
 *    `applyFile`）—— 避免「改了一半发现文件不对」。
 * 2. **换整张图之后要清历史**：由宿主在 `afterApply` 里做
 *    （见 `useUndoRedo.clearHistory` 的注释：不清的话 Ctrl+Z 会把
 *    上一份文档的图装进来）。
 */
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowNode as Node } from '@/graph/flowTypes'
import { toDocument } from '@/graph/serialization'
import type { DocumentEdge, SourceNode } from '@/graph/serialization'
import {
  TEMPLATE_KIND,
  buildTemplateFile,
  parseTemplateFile,
  templateFileName,
  type TemplateFile,
} from '@/graph/templateFile'
import { fromDocument } from '@/graph/serialization'
import { normalizeJsNodeRows } from '@/node/jsNodeType'
import type { EdgeView, NodeView } from './useSubgraphLevels'
import type { GraphNodeModel } from '@/types/node'

/** 调参面板那 5 个数 —— 只要求「能读能写」，不关心它是不是 reactive */
export interface ToolSettingsRef {
  socketSize: number
  rowHeight: number
  radius: number
  headerHeight: number
  labelRatio: number
}

export interface TemplateFileCtx {
  nodes: { value: Node[] }
  edges: { value: unknown[] }
  nodeViews: () => NodeView[]
  edgeViews: () => EdgeView[]
  tool: ToolSettingsRef
  makeFlowNode: (model: GraphNodeModel, position: { x: number; y: number }) => Node
  /**
   * 「这个节点类型来自哪个包」—— 写进文档的 `pkg`（见 `serialization.ts`）。
   *
   * ⚠️ 由宿主注入："有哪些包"是装配点才知道的事，而 `graph/` 不许依赖它
   * （`verify:layers` 守着的依赖方向）。
   */
  packageOf: (type: string) => string | undefined
  /** 重置图之后要落地的收尾：清选中、刷新图引擎等 */
  afterApply: () => void
  showNotice: (message: string) => void
}

export function useTemplateFile(ctx: TemplateFileCtx) {
  const {
    nodes,
    edges,
    nodeViews,
    edgeViews,
    tool,
    makeFlowNode,
    packageOf,
    afterApply,
    showNotice,
  } = ctx

function downloadJsonFile(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/** 当前这一版图的文档（含 design 字段、子图，以及逐节点的来源包 `pkg`） */
function currentDocument() {
  return toDocument(
    nodeViews() as unknown as SourceNode[],
    edgeViews() as unknown as (DocumentEdge & Record<string, unknown>)[],
    { packageOf },
  )
}

/**
 * 保存模板文件。
 *
 * `name` / `id` 除了生成文件名，还**写进文件**（`id` 就是文档 UUID）——
 * 2027 加的：文档库要靠它认得出"这份文件是不是库里已经有的那一份"
 * （用户原话：「加载文档的时候，如果发现 UUID 有重复的时候弹窗提醒是否覆盖」）。
 */
function saveTemplateFile(name: string, id: string) {
  const doc = currentDocument()
  const file = buildTemplateFile(doc, tool, { id, name })
  downloadJsonFile(templateFileName(name, id), file)
  showNotice(
    file.tool
      ? `已保存模板：${doc.nodes.length} 个节点 / ${doc.edges.length} 条边（含工具设置）`
      : `已保存模板：${doc.nodes.length} 个节点 / ${doc.edges.length} 条边`,
  )
}

/**
 * **当前图的模板文件**（文档库拿它落盘 / 导出）。
 *
 * ⚠️ 和 `saveTemplateFile` 共用 `buildTemplateFile` 这一处构造：
 * 两边各写一份的话，"导出"和"存进库里"迟早会漏字段
 * （表现是导出的文件再导进来少了点东西 —— 那种 bug 很难当场看出来）。
 */
function currentFile(meta?: { id?: string; name?: string }): TemplateFile {
  return buildTemplateFile(currentDocument(), tool, meta)
}

/**
 * **装进图之前，先把模型归一化一次**。
 *
 * 今天只有一件事：JS 节点的行按它自己的 `ports` / `code` 重建
 * （见 `node/jsNodeType.ts` 的 `normalizeJsNodeRows`）—— 老文档里存的是
 * "手动声明输出端口"那一版的行，直接装进来会和代码对不上。
 */
function normalizeRestoredModel(model: GraphNodeModel): GraphNodeModel {
  return normalizeJsNodeRows(model)
}

/**
 * **把一份已经解析好的文件装进图里**（文档库 / 打开文件都走它）。
 *
 * ⚠️ 这里**不弹确认**：该不该问"会替换当前画布"是**调用方**的事
 * （文档库那边已经问过一次了，再问一遍是噪音）——
 * 而且这个函数是纯"装进去"，好测。
 */
function applyFile(file: TemplateFile): boolean {
  const { nodes: restored, edges: restoredEdges } = fromDocument(file.document)

  const nextNodes = restored.map((n) =>
    makeFlowNode(
      normalizeRestoredModel((n.data as { model: GraphNodeModel }).model),
      n.position ?? { x: 0, y: 0 },
    ),
  ) as unknown as Node[]
  nodes.value = nextNodes
  edges.value = restoredEdges as unknown as unknown[]

  if (file.tool) Object.assign(tool, file.tool)

  afterApply()
  return true
}

  return {
    saveTemplateFile,
    /** 装一份**已解析**的文件（文档库那条路：先解析、先问冲突，再装） */
    applyFile,
    currentDocument,
    /** 当前图的模板文件（文档库落盘 / 导出共用一处构造） */
    currentFile,
    /** 把任意一份文档直接下载成 JSON（「导出设计文档」用） */
    downloadDocument: (name: string, id: string) =>
      downloadJsonFile(templateFileName(name, id), currentDocument()),
    /** 同一件事，但按**文档库那条路**构造（带 id / name，导出后能认回来） */
    downloadFile: (name: string, id: string) =>
      downloadJsonFile(templateFileName(name, id), currentFile({ id, name })),
  }
}

/** 供界面显示：文件认得出是不是我们的模板（调试用） */
export const TEMPLATE_KIND_FOR_UI = TEMPLATE_KIND
